import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import RequestAccess from './pages/RequestAccess';
import AcceptInvitation from './pages/AcceptInvitation';
import SetupAdmin from './pages/SetupAdmin';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Assessment from './pages/Assessment';
import MindMap from './pages/MindMap';
import Report from './pages/Report';
import UpgradePath from './pages/UpgradePath';
import Progress from './pages/Progress';
import Profile from './pages/Profile';
import Schedule from './pages/Schedule';

// Counselor Pages
import CounselorDashboard from './pages/counselor/CounselorDashboard';
import ClientView from './pages/counselor/ClientView';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';

// Layout
import AppLayout from './components/layout/AppLayout';
import CounselorLayout from './components/layout/CounselorLayout';
import AdminLayout from './components/layout/AdminLayout';

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground font-mono text-sm">Loading MindFlō...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/request-access" element={<RequestAccess />} />
      <Route path="/invite/:token" element={<AcceptInvitation />} />
      <Route path="/setup-admin" element={<SetupAdmin />} />
      <Route path="/register" element={<Navigate to="/request-access" replace />} />
      <Route path="/signup" element={<Navigate to="/request-access" replace />} />

      {/* Client Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout><Dashboard /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/assessment/:type" element={
        <ProtectedRoute>
          <AppLayout><Assessment /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/mind-map" element={
        <ProtectedRoute>
          <AppLayout><MindMap /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/report" element={
        <ProtectedRoute>
          <AppLayout><Report /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/report/:reportId" element={
        <ProtectedRoute>
          <AppLayout><Report /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/upgrade-path" element={
        <ProtectedRoute>
          <AppLayout><UpgradePath /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/progress" element={
        <ProtectedRoute>
          <AppLayout><Progress /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/schedule" element={
        <ProtectedRoute>
          <AppLayout><Schedule /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <AppLayout><Profile /></AppLayout>
        </ProtectedRoute>
      } />

      {/* Counselor Routes */}
      <Route path="/counselor" element={
        <ProtectedRoute requiredRole="counselor">
          <CounselorLayout><CounselorDashboard /></CounselorLayout>
        </ProtectedRoute>
      } />
      <Route path="/counselor/client/:clientId" element={
        <ProtectedRoute requiredRole="counselor">
          <CounselorLayout><ClientView /></CounselorLayout>
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requiredRole="admin">
          <AdminLayout><AdminDashboard /></AdminLayout>
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
